import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

const PRODUCTLY_VIOLET = '#7C3AED';

/* ─── Shared sub-components ───────────────────────────────────────────── */
const ProductlyTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/productly"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Productly
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: PRODUCTLY_VIOLET }}>
          <LayoutGrid size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Productly</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const ProductlyFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Productly · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/productly/terms" className="font-mono text-[0.55rem] tracking-widest text-[#7C3AED] uppercase">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/productly/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest text-[#7C3AED] uppercase shrink-0 mt-1.5">{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

const Point = ({ children }) => (
  <li className="text-[#374151] text-sm leading-relaxed flex items-start gap-2">
    <span className="w-1 h-1 rounded-full bg-[#7C3AED] shrink-0 mt-2" />
    {children}
  </li>
);

/* ─── Page ────────────────────────────────────────────────────────────── */
export const ProductlyTermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Productly Terms & Conditions"
        description="Terms and conditions for the Productly Shopify app by Smart Solutions."
        path="/apps/productly/terms"
      />
      <ProductlyTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Terms &amp; Conditions
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: May 2026
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8"
          >
            <P>
              Please read these Terms and Conditions carefully before using Productly. These Terms form a legal agreement
              between you (the Merchant) and Smart Solutions, the operator of Productly.
            </P>
          </motion.div>

          {/* Sections */}
          <Section num="01" title="Acceptance">
            <P>
              By installing or using Productly ("the App"), you agree to these Terms. If you do not agree, do not install
              or use the App.
            </P>
          </Section>

          <Section num="02" title="Eligibility">
            <P>
              You must be authorized to act on behalf of the Shopify store on which you install the App.
            </P>
            <P>
              Your store must comply with Shopify's Terms of Service and Acceptable Use Policy.
            </P>
          </Section>

          <Section num="03" title="What the App Does">
            <P>
              Productly enriches Shopify product pages with custom content blocks — spec tables, trust badges, tabbed content,
              FAQs, and extra media — configured from the Shopify admin.
            </P>
            <P>
              Outputs generated by the App may require your review before publishing. You are responsible for verifying
              content before it appears on your storefront.
            </P>
          </Section>

          <Section num="04" title="Billing">
            <P>
              The App is charged via Shopify Billing on a usage basis. You can cancel at any time by uninstalling the App.
            </P>
            <P>
              Credits, where applicable, are non-refundable.
            </P>
          </Section>

          <Section num="05" title="Acceptable Use">
            <ul className="space-y-2 mb-4">
              <Point>No illegal, infringing, deceptive, or harmful use of the App</Point>
              <Point>No reverse-engineering or abusing the service</Point>
              <Point>You are responsible for any content you publish using the App</Point>
            </ul>
          </Section>

          <Section num="06" title="Disclaimer">
            <P>
              The App is provided "as-is" without warranties of any kind. To the fullest extent permitted by law, Smart
              Solutions is not liable for any indirect or consequential damages arising from your use of the App.
            </P>
          </Section>

          <Section num="07" title="Termination">
            <P>
              You may uninstall the App at any time. We may suspend or terminate access for violations of these Terms.
            </P>
          </Section>

          <Section num="08" title="Changes">
            <P>
              We may update these Terms from time to time. Continued use of the App after changes become effective constitutes
              acceptance of the revised Terms.
            </P>
          </Section>

          <Section num="09" title="Contact">
            <P>
              If you have questions about these Terms, please contact us at:
            </P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" className="text-[#7C3AED] hover:underline">hello@smartsolution.ai</a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              These terms were last updated in May 2026
            </p>
            <Link
              to="/apps/productly/privacy"
              className="font-mono text-[0.6rem] tracking-widest text-[#7C3AED] hover:underline uppercase"
            >
              View Privacy Policy →
            </Link>
          </div>
        </div>
      </div>

      <ProductlyFooter />
    </div>
  );
};
